import { useRef, useState } from "react";
import { Button, FormInput } from "../../../components/ui";
/* Developer : Nithin
Date : 02-07-2026
changes: Added Validations for  email and phone                     */

export default function CompanyInfoTab({
  company,
  setCompany,
  onLogoChange,
  setLogoFile,
  uploadLogoOnly,
  setSignatureFile,
  uploadSignatureOnly,
  onSave,
  isSaving,
}) {
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const editableFields = ["name", "email", "phone", "address", "gst", "pan", "state", /* "stateCode", */ "website"];

  const validateField = (key, value) => {
    if (key === "phone" && value) {
      if (!/^\+91 \d{10}$/.test(value)) return "incorrect";
    }
    if (key === "email" && value) {
      if (!value.includes("@") || !value.includes(".com")) return "incorrect";
    }
    return "";
  };

  const handleSave = () => {
    let hasError = false;
    const newErrors = {};
    editableFields.forEach(key => {
      const err = validateField(key, company[key]);
      if (err) {
        newErrors[key] = err;
        hasError = true;
      }
    });
    setErrors(newErrors);
    if (!hasError) onSave();
  };

  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    // Immediately show preview in the tab
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onLogoChange(reader.result);
    };
    reader.readAsDataURL(file);
    event.target.value = "";

    // Automatically upload and save it
    if (typeof uploadLogoOnly === "function") {
      uploadLogoOnly(file);
    }
  };

  const handleSignatureFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setCompany((prev) => ({ ...prev, digitalSignatureUrl: reader.result }));
      }
    };
    reader.readAsDataURL(file);
    event.target.value = "";

    if (typeof uploadSignatureOnly === "function") {
      uploadSignatureOnly(file);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-800">Company Information</h3>
        <p className="text-sm text-slate-500">
          Manage your company profile and general contact information
        </p>
      </div>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 bg-brand-blue rounded-2xl flex items-center justify-center overflow-hidden">
          {company.logoUrl ? (
            <img src={company.logoUrl} alt="Company Logo" className="w-full h-full object-cover" />
          ) : (
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
            </svg>
          )}
        </div>
        <div>
          <div className="font-bold text-brand-dark text-lg">{company.name}</div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoFile}
          />
          <Button
            variant="secondary"
            size="xs"
            className="mt-1"
            onClick={() => fileInputRef.current?.click()}
          >
            Upload Logo
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {editableFields.map((key) => {
          let labelStr = key.replace(/([A-Z])/g, " $1").replace(/\b\w/g, (char) => char.toUpperCase());
          if (key === "gst") labelStr = "GST";
          if (key === "pan") labelStr = "PAN";

          return (
            <FormInput
              key={key}
              field={key}
              label={labelStr}
              value={company[key] || ""}
              error={errors[key]}
              onChange={(e) => {
                const val = e.target.value;
                setCompany((prev) => ({ ...prev, [key]: val }));
                setErrors((prev) => ({ ...prev, [key]: validateField(key, val) }));
              }}
            />
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-6 mb-6">
        <div className="w-16 h-16 bg-brand-blue/10 border border-brand-blue/30 rounded-2xl flex items-center justify-center overflow-hidden">
          {company.digitalSignatureUrl ? (
            <img src={company.digitalSignatureUrl} alt="Digital Signature" className="w-full h-full object-cover" />
          ) : (
            <svg
              className="w-8 h-8 text-brand-blue"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          )}
        </div>
        <div>
          <div className="font-bold text-brand-dark text-lg text-slate-700 text-sm mb-1">Digital Signature</div>
          <input
            ref={signatureInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleSignatureFile}
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => signatureInputRef.current?.click()}
            >
              Upload Signature
            </Button>
            {company.digitalSignatureUrl && (
              <Button
                variant="ghost-danger"
                size="xs"
                onClick={() => setCompany((prev) => ({ ...prev, digitalSignatureUrl: "" }))}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
      <Button variant="orange" size="md" onClick={handleSave} loading={isSaving}>
        Save Changes
      </Button>
    </div>
  );
}
